import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@app/libs/utils';

import ArrowDownIcon from '../icons/ArrowDownIcon';

interface ThumbnailProps {
  images: string[];
  alt: string;
  width: number;
  height: number;
  className?: string;
  imageClassName?: string;
}

export const Thumbnail: React.FC<ThumbnailProps> = ({
  images,
  alt,
  width,
  height,
  className,
  imageClassName,
}) => {
  const [noImage, setNoImage] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const updateScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current && canScrollRight) {
      // Find the first image element and use its clientWidth for scrolling
      const firstImg = scrollRef.current.querySelector('img');
      const scrollAmount = firstImg ? firstImg.clientWidth : 0;
      scrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleScrollLeft = () => {
    if (scrollRef.current && canScrollLeft) {
      const firstImg = scrollRef.current.querySelector('img');
      const scrollAmount = firstImg ? firstImg.clientWidth : 0;
      scrollRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (!images?.length) {
      setNoImage(true);
    }
  }, [images]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    // Initial check
    updateScrollButtons();

    // Add scroll listener
    const handleScroll = () => {
      updateScrollButtons();
    };

    scrollElement.addEventListener('scroll', handleScroll);

    // Add resize listener to handle window resize
    const handleResize = () => {
      updateScrollButtons();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [images]);

  return (
    <div className={cn('relative flex w-full h-full', className)}>
      <div
        ref={scrollRef}
        className="flex flex-nowrap overflow-y-hidden overflow-x-scroll w-full h-full scrollbar-hide snap-x snap-mandatory"
      >
        {noImage ? (
          <Image
            src={'/images/no-image.png'}
            alt={alt || 'No image'}
            width={width}
            height={height}
            className={cn('w-full h-full object-cover', imageClassName)}
          />
        ) : (
          images?.map(image => (
            <Image
              key={image}
              src={`/api/v1/images/${image}`}
              alt={alt}
              width={width}
              height={height}
              className={cn('min-w-full h-full object-cover snap-center', imageClassName)}
            />
          ))
        )}
      </div>
      {images?.length > 1 && (
        <>
          <button
            className={cn(
              'absolute right-0 top-1/2 -translate-y-1/2 z-10 transition-opacity duration-200',
              canScrollRight ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
            )}
            onClick={handleScrollRight}
            disabled={!canScrollRight}
          >
            <ArrowDownIcon className="w-6 h-6 rotate-270" />
          </button>

          <button
            className={cn(
              'absolute left-0 top-1/2 -translate-y-1/2 z-10 transition-opacity duration-200',
              canScrollLeft ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
            )}
            onClick={handleScrollLeft}
            disabled={!canScrollLeft}
          >
            <ArrowDownIcon className="w-6 h-6 rotate-90" />
          </button>
        </>
      )}
    </div>
  );
};
